import { getNearbyCorporates } from '../../../api/studiosApi'
import CSVExporter from '../../../components/common/csv-exporter'
import TencentMap from '../../../components/tencent-map/tencent-map'
import translate from '../../../i18n'
import { INearbyCorporateLocation, INearbyCorporatesResponse } from '../share-type'
import styles from './nearby-corporates.module.less'
import { SearchOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  InputNumber,
  notification,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

interface SearchValues {
  distanceKm: number
}

interface CorporateFilterValues {
  corporateId?: number | null
  corporateName?: string
}

const DEFAULT_DISTANCE_KM = 3
const DEFAULT_PAGE_SIZE = 20
const EMPTY_LOCATIONS: INearbyCorporateLocation[] = []

const textCell = (value?: string | null) => (
  <Tooltip title={value || '-'} placement="topLeft">
    <span className={styles['single-line-cell']}>{value || '-'}</span>
  </Tooltip>
)

const NearbyCorporates = () => {
  const studioId = Number(useParams().id)
  const [form] = Form.useForm<SearchValues>()
  const [filterForm] = Form.useForm<CorporateFilterValues>()
  const [response, setResponse] = useState<INearbyCorporatesResponse>()
  const [distanceKm, setDistanceKm] = useState(DEFAULT_DISTANCE_KM)
  const [filters, setFilters] = useState<CorporateFilterValues>({})
  const [selectedLocationId, setSelectedLocationId] = useState<number>()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [loading, setLoading] = useState(false)

  const search = useCallback(
    (distance: number) => {
      setLoading(true)
      setSelectedLocationId(undefined)
      return getNearbyCorporates(studioId, distance)
        .then(result => {
          setResponse(result)
          setDistanceKm(result.distanceKm)
          setFilters({})
          filterForm.resetFields()
          setCurrentPage(1)
        })
        .catch(error =>
          notification.error({
            message: translate('corporate.nearbyStudios.loadFailed'),
            description: error?.message,
          }),
        )
        .finally(() => setLoading(false))
    },
    [filterForm, studioId],
  )

  useEffect(() => {
    form.setFieldValue('distanceKm', DEFAULT_DISTANCE_KM)
    search(DEFAULT_DISTANCE_KM)
  }, [form, search])

  const locations = response?.corporateLocations || EMPTY_LOCATIONS
  const mapPoints = useMemo(
    () =>
      locations.map(location => ({
        id: location.locationId,
        latitude: location.latitude,
        longitude: location.longitude,
      })),
    [locations],
  )
  const filteredLocations = useMemo(() => {
    const name = filters.corporateName?.trim().toLowerCase()
    return locations.filter(location => {
      const matchesId =
        filters.corporateId === undefined ||
        filters.corporateId === null ||
        location.corporateId === filters.corporateId
      return matchesId && (!name || location.corporateName.toLowerCase().includes(name))
    })
  }, [filters, locations])

  const handleSelect = useCallback(
    (locationId: number) => {
      setSelectedLocationId(locationId)
      const index = filteredLocations.findIndex(location => location.locationId === locationId)
      if (index >= 0) {
        setCurrentPage(Math.floor(index / pageSize) + 1)
      }
    },
    [filteredLocations, pageSize],
  )

  const columns: ColumnsType<INearbyCorporateLocation> = [
    {
      title: 'ID',
      dataIndex: 'corporateId',
      key: 'corporateId',
      width: 70,
      render: value => (
        <Link to={`/corporates/${value}`} onClick={event => event.stopPropagation()}>
          {value}
        </Link>
      ),
    },
    {
      title: translate('studio.nearbyCorporates.corporateName'),
      dataIndex: 'corporateName',
      key: 'corporateName',
      width: 130,
      render: textCell,
    },
    {
      title: translate('studio.nearbyCorporates.location'),
      key: 'location',
      width: 260,
      render: (_, record) =>
        textCell(
          [record.cityName, record.businessDistrictName, record.address]
            .filter(Boolean)
            .join(' / '),
        ),
    },
    { title: translate('corporate.location.memberCount'), dataIndex: 'memberCount', width: 130 },
    {
      title: translate('studio.nearbyCorporates.price'),
      dataIndex: 'price',
      width: 100,
      render: value => (value === null ? '-' : (value / 100).toFixed(2)),
    },
    {
      title: translate('corporate.distributionFrequency'),
      dataIndex: 'distributionFrequency',
      width: 110,
      render: textCell,
    },
    {
      title: translate('studio.nearbyCorporates.maxCredits'),
      dataIndex: 'maxCreditsUsageThreshold',
      width: 150,
      render: value => value ?? '-',
    },
    {
      title: translate('studio.nearbyCorporates.disallowActivities'),
      dataIndex: 'disallowActivityList',
      width: 180,
      render: value => textCell(value.length ? value.join(', ') : '-'),
    },
    {
      title: translate('corporate.distributionRuleRemark'),
      dataIndex: 'distributionRuleRemark',
      width: 160,
      render: textCell,
    },
    {
      title: translate('corporate.principal'),
      dataIndex: 'principal',
      width: 110,
      render: textCell,
    },
  ]

  const exportData = filteredLocations.map(location => ({
    企业ID: location.corporateId,
    企业名称: location.corporateName,
    企业位置ID: location.locationId,
    企业地址: [location.cityName, location.businessDistrictName, location.address]
      .filter(Boolean)
      .join(' / '),
    城市: location.cityName,
    商圈: location.businessDistrictName,
    地址: location.address,
    该职场参与人数: location.memberCount,
    采购单价: location.price === null ? '' : (location.price / 100).toFixed(2),
    发放频率: location.distributionFrequency || '',
    单次预约点数限制: location.maxCreditsUsageThreshold ?? '',
    '不允许预约的 activities': location.disallowActivityList.join(', '),
    发放规则备注: location.distributionRuleRemark || '',
    负责人: location.principal || '',
    '距离（公里）': location.distanceKm.toFixed(2),
  }))

  return (
    <div className={styles.page}>
      <div className={styles['page-heading']}>
        <Typography.Text type="secondary">
          {translate('studio.nearbyCorporates.currentStudio')}
        </Typography.Text>
        <Typography.Text strong className={styles['corporate-name']}>
          {response?.studio.name || '-'}
        </Typography.Text>
      </div>
      <Form
        className={styles.filters}
        form={form}
        layout="inline"
        onFinish={values => search(values.distanceKm)}
      >
        <Form.Item
          name="distanceKm"
          label={translate('corporate.nearbyStudios.distance')}
          rules={[{ required: true, type: 'number', min: 0.0000001 }]}
        >
          <InputNumber
            min={0.0000001}
            step={0.1}
            addonAfter={translate('corporate.nearbyStudios.distanceUnit')}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} icon={<SearchOutlined />}>
            {translate('corporate.nearbyStudios.search')}
          </Button>
        </Form.Item>
        <Form.Item>
          <CSVExporter
            data={exportData}
            filename={`studio-nearby-corporates-${studioId}-${dayjs().format('YYYYMMDDHHmmss')}.csv`}
          >
            {translate('studio.nearbyCorporates.export')}
          </CSVExporter>
        </Form.Item>
      </Form>
      <div className={styles.content}>
        <div className={styles.map}>
          {response && (
            <TencentMap
              location={response.studio}
              studios={mapPoints}
              distanceKm={distanceKm}
              selectedStudioId={selectedLocationId}
              onStudioSelect={handleSelect}
            />
          )}
        </div>
        <div className={styles.list}>
          <Form
            className={styles['list-filters']}
            form={filterForm}
            layout="inline"
            onFinish={values => {
              setFilters(values)
              setSelectedLocationId(undefined)
              setCurrentPage(1)
            }}
          >
            <Form.Item name="corporateId" label="ID">
              <InputNumber min={1} precision={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item
              name="corporateName"
              label={translate('studio.nearbyCorporates.corporateName')}
            >
              <Input allowClear style={{ width: 150 }} />
            </Form.Item>
            <Form.Item>
              <Space size={4}>
                <Button htmlType="submit" icon={<SearchOutlined />}>
                  {translate('corporate.nearbyStudios.search')}
                </Button>
                <Button
                  onClick={() => {
                    filterForm.resetFields()
                    setFilters({})
                    setSelectedLocationId(undefined)
                    setCurrentPage(1)
                  }}
                >
                  {translate('reset')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
          <Table
            rowKey="locationId"
            size="small"
            tableLayout="fixed"
            loading={loading}
            columns={columns}
            dataSource={filteredLocations}
            scroll={{ x: 1400, y: 'calc(100vh - 480px)' }}
            rowClassName={record =>
              record.locationId === selectedLocationId ? styles['selected-row'] : ''
            }
            onRow={record => ({ onClick: () => handleSelect(record.locationId) })}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredLocations.length,
              size: 'small',
              showSizeChanger: true,
              onChange: (page, size) => {
                setCurrentPage(page)
                setPageSize(size)
              },
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default NearbyCorporates
